<template>
  <Modal
    v-model="visibleSync"
    :title="modalTitle"
    class-name="intent-rating-rule-modal"
    width="560"
    @on-visible-change="onVisibleChange"
  >
    <div class="intent-modal-form">
      <div class="intent-modal-field">
        <label class="intent-modal-field__label">
          <span class="intent-modal-field__required">*</span>
          意向标签名称
        </label>
        <Input
          v-model="form.name"
          class="intent-modal-field__input"
          placeholder="如：A完整表单、G无意向"
          :maxlength="80"
        />
      </div>

      <div class="intent-modal-field">
        <label class="intent-modal-field__label">
          <span class="intent-modal-field__required">*</span>
          匹配条件
        </label>
        <div class="intent-modal-field__control">
          <div class="rating-conditions">
            <div
              v-for="(cond, index) in form.conditions"
              :key="'cond-' + index"
              class="rating-conditions__row"
            >
              <Select
                v-model="cond.fieldId"
                class="rating-conditions__field"
                transfer
                transfer-class-name="intent-rating-select-dropdown"
                placeholder="请选择字段"
                @on-change="onFieldChange(cond)"
              >
                <OptionGroup v-if="collectFieldOptions.length" label="采集字段">
                  <Option
                    v-for="field in collectFieldOptions"
                    :key="field.fieldId"
                    :value="field.fieldId"
                    :label="field.name"
                  />
                </OptionGroup>
                <OptionGroup label="系统指标">
                  <Option
                    v-for="field in systemFieldOptions"
                    :key="field.fieldId"
                    :value="field.fieldId"
                    :label="field.name"
                  />
                </OptionGroup>
              </Select>
              <Select
                v-model="cond.operator"
                class="rating-conditions__operator"
                transfer
                transfer-class-name="intent-rating-select-dropdown"
                :disabled="!cond.fieldId"
                @on-change="onOperatorChange(cond)"
              >
                <Option
                  v-for="op in getOperatorOptions(cond)"
                  :key="op"
                  :value="op"
                  :label="getIntentOperatorLabel(op)"
                />
              </Select>
              <Select
                v-if="useValueSelect(cond)"
                v-model="cond.value"
                class="rating-conditions__value"
                transfer
                transfer-class-name="intent-rating-select-dropdown"
                placeholder="请选择"
                :disabled="!isValueEditable(cond)"
              >
                <Option
                  v-for="item in getEnumOptions(cond.fieldId)"
                  :key="item"
                  :value="item"
                  :label="item"
                />
              </Select>
              <Input
                v-else
                v-model="cond.value"
                class="rating-conditions__value"
                :type="getValueInputType(cond)"
                :placeholder="getValuePlaceholder(cond)"
                :disabled="!isValueEditable(cond)"
                :min="getValueInputType(cond) === 'number' ? 0 : undefined"
              />
              <DeleteIconButton size="sm" title="删除" @click="removeCondition(index)" />
            </div>
            <Button type="dashed" long class="rating-conditions__add" @click="addCondition">
              + 添加条件
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="intent-modal-footer">
      <Button @click="close">取消</Button>
      <Button type="primary" @click="submit">保存</Button>
    </div>
  </Modal>
</template>

<script>
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import {
  EXTRACT_FIELD_TYPE,
  INTENT_CONDITION_OPERATOR,
  INTENT_SYSTEM_FIELDS,
} from '../../constants/intentTag'
import {
  defaultOpForRatingRuleFieldId,
  findRatingRuleField,
  getIntentOperatorLabel,
  getOpsForRatingRuleField,
  getSystemFieldValuePlaceholder,
  isSystemFieldId,
  normalizeRatingConditionForField,
} from '../../utils/intentTagHelpers'

function createEmptyCondition(extractFields) {
  return normalizeRatingConditionForField(
    {
      fieldId: '',
      operator: INTENT_CONDITION_OPERATOR.EQ,
      value: '',
    },
    extractFields,
  )
}

function createEmptyForm(extractFields) {
  return {
    name: '',
    conditions: [createEmptyCondition(extractFields)],
  }
}

export default {
  name: 'RatingRuleModal',
  components: { DeleteIconButton },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editIndex: {
      type: Number,
      default: -1,
    },
    initialData: {
      type: Object,
      default: null,
    },
    extractFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: createEmptyForm([]),
    }
  },
  computed: {
    visibleSync: {
      get() {
        return this.value
      },
      set(next) {
        this.$emit('input', next)
      },
    },
    modalTitle() {
      return this.editIndex >= 0 ? '编辑评级规则' : '添加评级规则'
    },
    collectFieldOptions() {
      return (Array.isArray(this.extractFields) ? this.extractFields : []).filter(
        (field) => field && field.fieldId && !isSystemFieldId(field.fieldId),
      )
    },
    systemFieldOptions() {
      return INTENT_SYSTEM_FIELDS
    },
  },
  methods: {
    getIntentOperatorLabel,
    onVisibleChange(open) {
      if (!open) return
      this.resetForm()
    },
    resetForm() {
      const src = this.initialData && typeof this.initialData === 'object' ? this.initialData : null
      if (src) {
        const conditions = Array.isArray(src.conditions) && src.conditions.length
          ? src.conditions.map((item) =>
              normalizeRatingConditionForField(
                {
                  fieldId: item.fieldId != null ? String(item.fieldId) : '',
                  operator: item.operator,
                  value: item.value != null ? String(item.value) : '',
                },
                this.extractFields,
              ),
            )
          : [createEmptyCondition(this.extractFields)]
        this.form = {
          name: src.name != null ? String(src.name) : '',
          conditions,
        }
      } else {
        this.form = createEmptyForm(this.extractFields)
      }
    },
    getFieldById(fieldId) {
      return findRatingRuleField(fieldId, this.extractFields)
    },
    getOperatorOptions(cond) {
      return getOpsForRatingRuleField(cond.fieldId, this.extractFields)
    },
    getEnumOptions(fieldId) {
      const field = this.getFieldById(fieldId)
      if (!field || !Array.isArray(field.allowedValues)) return []
      return field.allowedValues
    },
    isValueEditable(cond) {
      if (!String(cond.fieldId || '').trim()) return false
      if (
        cond.operator === INTENT_CONDITION_OPERATOR.FILLED ||
        cond.operator === INTENT_CONDITION_OPERATOR.EMPTY
      ) {
        return false
      }
      return true
    },
    useValueSelect(cond) {
      if (
        cond.operator === INTENT_CONDITION_OPERATOR.FILLED ||
        cond.operator === INTENT_CONDITION_OPERATOR.EMPTY
      ) {
        return false
      }
      const field = this.getFieldById(cond.fieldId)
      if (!field || field.type !== EXTRACT_FIELD_TYPE.ENUM) return false
      return this.getEnumOptions(cond.fieldId).length > 0
    },
    getValueInputType(cond) {
      const field = this.getFieldById(cond.fieldId)
      if (field && field.type === EXTRACT_FIELD_TYPE.NUMBER) return 'number'
      return 'text'
    },
    getValuePlaceholder(cond) {
      const field = this.getFieldById(cond.fieldId)
      if (!field) return '输入值'
      if (field.source === 'system') return getSystemFieldValuePlaceholder(field)
      if (field.type === EXTRACT_FIELD_TYPE.NUMBER) {
        return field.unit ? `输入${field.unit}数` : '输入数值'
      }
      return '输入值'
    },
    onFieldChange(cond) {
      cond.operator = defaultOpForRatingRuleFieldId(cond.fieldId, this.extractFields)
      cond.value = ''
      normalizeRatingConditionForField(cond, this.extractFields)
    },
    onOperatorChange(cond) {
      if (
        cond.operator === INTENT_CONDITION_OPERATOR.FILLED ||
        cond.operator === INTENT_CONDITION_OPERATOR.EMPTY
      ) {
        cond.value = ''
      }
    },
    addCondition() {
      this.form.conditions.push(createEmptyCondition(this.extractFields))
    },
    removeCondition(index) {
      if (this.form.conditions.length <= 1) {
        this.$Message.warning('至少保留一个匹配条件')
        return
      }
      this.form.conditions.splice(index, 1)
    },
    close() {
      this.visibleSync = false
    },
    submit() {
      const name = String(this.form.name || '').trim()
      if (!name) {
        this.$Message.warning('请填写意向标签名称')
        return
      }
      const conditions = (this.form.conditions || [])
        .map((cond) => normalizeRatingConditionForField(cond, this.extractFields))
        .filter((cond) => cond.fieldId)
      if (!conditions.length) {
        this.$Message.warning('请至少添加一个有效匹配条件')
        return
      }
      for (let i = 0; i < conditions.length; i += 1) {
        const cond = conditions[i]
        const needsValue =
          cond.operator !== INTENT_CONDITION_OPERATOR.FILLED &&
          cond.operator !== INTENT_CONDITION_OPERATOR.EMPTY
        if (needsValue && !String(cond.value || '').trim()) {
          this.$Message.warning('请完善匹配条件中的取值')
          return
        }
      }
      this.$emit('submit', { name, conditions })
      this.visibleSync = false
    },
  },
}
</script>

<style lang="scss" scoped>
.intent-modal-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.intent-modal-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.intent-modal-field__label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #262626;
  font-weight: 400;
}

.intent-modal-field__required {
  margin-right: 2px;
  color: #ed4014;
}

.intent-modal-field__control,
.intent-modal-field__input {
  width: 100%;
}

.rating-conditions {
  width: 100%;
}

.rating-conditions__row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.rating-conditions__field {
  flex: 1.2;
  min-width: 0;
}

.rating-conditions__operator {
  width: 112px;
  flex-shrink: 0;
}

.rating-conditions__value {
  flex: 1;
  min-width: 0;

  ::v-deep input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  ::v-deep input[type='number']::-webkit-outer-spin-button,
  ::v-deep input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.rating-conditions__add {
  margin-top: 4px;
}

.intent-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.intent-rating-rule-modal {
  .ivu-modal-body {
    padding: 20px 24px 8px;
  }

  .ivu-modal-footer {
    border-top: none;
    padding: 8px 24px 20px;
  }
}

.intent-rating-select-dropdown.ivu-select-dropdown {
  text-align: left;

  .ivu-select-group-title {
    padding-left: 12px;
    font-size: 14px;
    color: #909399;
  }

  .ivu-select-item {
    text-align: left;
    font-size: 14px;
    padding: 7px 12px 7px 24px;
  }

  .ivu-select-item-selected,
  .ivu-select-item-selected:hover {
    color: #5668f0;
    font-weight: 500;
  }
}
</style>
